import { useDataTable } from "../../../../shared/hooks/DataTable";
import { InvoiceSummaryService } from "../../InvoiceSummaryService";
import { useState, useEffect } from "react";
import {
  DataPeriod,
  ODataPeriod,
  OInvoiceLookUpField,
  prepareInvoiceStatuses,
  SummaryDataDetails,
} from "../../../../shared/constants";
import { SummaryDetailsTableProps } from "./summaryDetailsTableConsts";
import { InvoiceLookUpTableDialog } from "./lookupTableDialog";
import { useImExExcel } from "../../../../shared/hooks/ImExExcel";
import { useToolbar } from "../../../../shared/hooks/Toolbar";
import {
  combineSummaryDetails,
  formatVatRate,
  invoiceStatusClassMap,
  prepareInvoiceStatusClassMap,
} from "../../invoiceSummaryUtils";
import { InvoiceService } from "../../../hoa-don/InvoiceService";
import { IamColumn } from "../../../../shared/hooks/DataTableBasic";
import { FormattedCurrency } from "../../../../shared/component/FormattedCurrency";
import { ColumnGroup } from "primereact/columngroup";
import { Row } from "primereact/row";
import { Column } from "primereact/column";
import { SumColumn } from "./sumColumn";
import moment from "moment";

export const SummaryDetailsTable = ({
  formState,
  id,
  serial,
  isEditMode,
  setSummaryDetails,
  summaryDetails,
}: SummaryDetailsTableProps) => {
  const [lookupData, setLookupData] = useState<Record<string, any>[]>([]);
  const [openLookup, setOpenLookup] = useState<boolean>(false);
  const [currSummaryDetails, setCurrSummaryDetails] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [totalAmountWithoutVAT, setTotalAmountWithoutVAT] = useState(0);
  const [totalVatAmount, setTotalVatAmount] = useState(0);

  const summaryDetailColumns: IamColumn[] = [
    {
      field: OInvoiceLookUpField.INVOICE_SERIAL,
      header: "Ký hiệu",
      width: 120,
      sortable: false,
    },
    {
      field: OInvoiceLookUpField.INVOICE_NUMBER,
      header: "Số hóa đơn",
      width: 120,
      sortable: false,
    },
    {
      field: OInvoiceLookUpField.INVOICE_DATE,
      header: "Ngày hóa đơn",
      width: 120,
      sortable: false,
      dataType: "custom",
      customCell: (item) => (
        <span>
          {item.invoiceDate
            ? new Date(item.invoiceDate).toLocaleDateString("vi-VN")
            : ""}
        </span>
      ),
    },
    {
      field: OInvoiceLookUpField.BUYER_NAME,
      header: "Tên đơn vị/Tên người mua",
      width: 250,
      sortable: false,
    },
    {
      field: OInvoiceLookUpField.BUYER_TAX_CODE,
      header: "Mã số thuế người mua",
      width: 170,
      sortable: false,
    },
    {
      field: OInvoiceLookUpField.BUYER_BUDGET_UNIT_CODE,
      header: "Mã đơn vị quan hệ ngân sách",
      sortable: false,
      width: 190,
    },
    {
      field: OInvoiceLookUpField.PRODUCT_CODE,
      header: "Mã hàng",
      sortable: false,
      width: 100,
    },
    {
      field: OInvoiceLookUpField.PRODUCT_NAME,
      header: "Tên hàng hóa, dịch vụ",
      width: 150,
      sortable: false,
    },
    {
      field: OInvoiceLookUpField.UNIT_CODE,
      header: "Đơn vị tính",
      width: 100,
      sortable: false,
    },
    {
      field: OInvoiceLookUpField.QUANTITY,
      header: "Số lượng hàng hóa",
      width: 120,
      sortable: false,
    },
    {
      field: OInvoiceLookUpField.AMOUNT_WITHOUT_VAT,
      header: "Tiền trước thuế",
      width: 120,
      sortable: false,
      dataType: "custom",
      customCell: (item) => (
        <FormattedCurrency
          value={item.amountWithoutVAT}
          currency={formState.currencyCode}
        />
      ),
    },
    {
      field: OInvoiceLookUpField.VAT_RATE,
      header: "Thuế suất",
      width: 120,
      sortable: false,
      dataType: "custom",
      customCell: (item) => (
        <span>
          {item.vatName
            ? formatVatRate(item.vatName)
            : formatVatRate(item.vatRate)}
        </span>
      ),
    },
    {
      field: OInvoiceLookUpField.TOTAL_DISCOUNT_AMOUNT,
      header: "Tổng giảm trừ khác",
      width: 120,
      sortable: false,
      dataType: "custom",
      customCell: (item) => (
        <FormattedCurrency
          value={item.totalDiscountAmount}
          currency={formState.currencyCode}
        />
      ),
    },
    {
      field: OInvoiceLookUpField.VAT_AMOUNT,
      header: "Tiền thuế",
      width: 120,
      sortable: false,
      dataType: "custom",
      customCell: (item) => (
        <FormattedCurrency
          value={item.vatAmount}
          currency={formState.currencyCode}
        />
      ),
    },
    {
      field: OInvoiceLookUpField.TOTAL_AMOUNT,
      header: "Tiền thanh toán",
      width: 120,
      sortable: false,
      dataType: "custom",
      customCell: (item) => (
        <FormattedCurrency
          value={item.totalAmount}
          currency={formState.currencyCode}
        />
      ),
    },
    {
      field: OInvoiceLookUpField.EXCHANGE_RATE,
      header: "Tỷ giá",
      width: 120,
      sortable: false,
      dataType: "custom",
      customCell: (item) => (
        <FormattedCurrency
          value={item.exchangeRate}
          currency={formState.currencyCode}
        />
      ),
    },
    {
      field: OInvoiceLookUpField.TOTAL_FEE,
      header: "Tổng tiền phí",
      width: 120,
      sortable: false,
      dataType: "custom",
      customCell: (item) => (
        <FormattedCurrency
          value={item.totalFee}
          currency={formState.currencyCode}
        />
      ),
    },
    {
      field: OInvoiceLookUpField.INVOICE_STATUS,
      header: "Trạng thái hóa đơn",
      dataType: "custom",
      customCell: (item) => (
        <span
          className={`invoice-status invoice-status-${prepareInvoiceStatusClassMap(
            item.invoiceStatus
          )}`}
        >
          {
            prepareInvoiceStatuses.find(
              (status) => item.invoiceStatus === status.value
            ).label
          }
        </span>
      ),
      width: 120,
      sortable: false,
    },
    {
      field: OInvoiceLookUpField.RELATED_INVOICE_TYPE,
      header: "Loại hóa đơn liên quan",
      width: 170,
      sortable: false,
    },
    {
      field: OInvoiceLookUpField.RELATED_INVOICE_SERIAL,
      header: "Mẫu số hóa đơn liên quan",
      width: 170,
      sortable: false,
    },
    {
      field: OInvoiceLookUpField.RELATED_TEMPLATE_CODE,
      header: "Ký hiệu hóa đơn liên quan",
      width: 170,
      sortable: false,
    },
    {
      field: OInvoiceLookUpField.RELATED_INVOICE_NUMBER,
      header: "Số hóa đơn liên quan",
      width: 170,
      sortable: false,
    },
    {
      field: OInvoiceLookUpField.ADJUSTMENT_PERIOD_TYPE,
      header: "Loại kỳ dữ liệu điều chỉnh",
      width: 160,
      sortable: false,
    },
    {
      field: OInvoiceLookUpField.ADJUSTMENT_PERIOD_VALUE,
      header: "Kỳ dữ liệu điều chỉnh",
      width: 150,
      sortable: false,
    },
    {
      field: OInvoiceLookUpField.NOTE,
      header: "Ghi chú",
      width: 150,
      sortable: false,
    },
  ];

  const tableLastRow = (
    <ColumnGroup>
      <Row>
        <Column
          style={{
            justifyContent: "end",
            minWidth: 50,
            maxWidth: 50,
          }}
        />
        {summaryDetailColumns.map((column, index) => {
          return (
            // index columns
            <Column
              key={index}
              footer={
                <SumColumn
                  field={column.field}
                  value={{ totalAmount, totalAmountWithoutVAT, totalVatAmount }}
                />
              }
              style={{
                justifyContent: "end",
                minWidth: `${column.width}px`,
                maxWidth: `${column.width}px`,
              }}
            />
          );
        })}
      </Row>
    </ColumnGroup>
  );

  const {
    render: renderDataTable,
    reloadLazyData,
    selectedItems,
  } = useDataTable({
    indexColumnWidth: 50,
    heightCalc: 320,
    columns: summaryDetailColumns,
    footerColumnGroup: tableLastRow,
    hideRowsPerPageDropdown: true,
    hideCurrentPageReport: true,
    getList: () => {
      const sums = {
        totalFee: 0,
        totalAmount: 0,
        totalAmountWithoutVAT: 0,
        totalVatAmount: 0,
      };
      summaryDetails.forEach((item) => {
        sums.totalFee += item.totalFee;
        sums.totalAmount += item.totalAmount;
        sums.totalAmountWithoutVAT += item.amountWithoutVAT;
        sums.totalVatAmount += item.vatAmount;
      });
      setTotalAmount(sums.totalAmount);
      setTotalAmountWithoutVAT(sums.totalAmountWithoutVAT);
      setTotalVatAmount(sums.totalVatAmount);

      const indexingList = summaryDetails.map((item, index) => {
        // set index for delete button
        return {
          ...item,
          index: index,
        };
      });

      return Promise.resolve({
        items: indexingList,
        totalCount: summaryDetails.length,
      });
    },
  });

  const { renderImExExcel, showImportDialog } = useImExExcel({
    exportTemplate: () => {
      return InvoiceSummaryService.getExcelTemplate();
    },
    importExcel: (data) => {
      return InvoiceSummaryService.uploadExcel(data);
    },
    exportExcel() {
      return Promise.resolve();
    },
    reloadLazyData(result) {
      if (Array.isArray(result)) {
        setSummaryDetails(summaryDetails.concat(result));
        reloadLazyData();
      }
    },
    exportExcelFileName: "bang-tong-hop",
  });

  const { renderToolbar } = useToolbar({
    selectedItems,
    create() {
      setOpenLookup(true);
    },
    style: {
      borderWidth: 0,
    },
    otherActions: [
      {
        label: "Xóa bản ghi",
        icon: "pi pi-trash",
        className: "iam-toolbar iam-toolbar-danger",
        disabled:
          (Array.isArray(selectedItems) && selectedItems.length === 0) ||
          !selectedItems,
        command: () => {
          // imported invoices from excel dont have invoice id
          // so we need to find the index of the item in the summaryDetails array
          if (Array.isArray(selectedItems)) {
            setSummaryDetails(
              summaryDetails.filter(
                (_item, index) =>
                  !(
                    selectedItems as (SummaryDataDetails & { index: number })[]
                  ).some((selected) => selected.index === index)
              )
            );
            reloadLazyData();
            return Promise.resolve();
          }

          setSummaryDetails(
            summaryDetails.filter((_item, index) => {
              return (
                index !==
                (selectedItems as SummaryDataDetails & { index: number }).index
              );
            })
          );
          reloadLazyData();
        },
      },
      {
        label: "Nhập từ Excel",
        icon: "pi pi-file-excel",
        className: "iam-toolbar iam-toolbar-success",
        command: () => {
          showImportDialog();
        },
      },
    ],
  });

  useEffect(() => {
    if (lookupData.length > 0) {
      InvoiceSummaryService.getSummaryDetails({
        invoiceIdList: lookupData.map((item) => item.invoiceId),
        multipleTax: formState.multiTax,
        dataPeriod: formState.dataPeriod as DataPeriod,
        ...(formState.dataPeriod === ODataPeriod.DAY
          ? {
              dataPeriodDate: moment(formState.dataPeriodDate).format(
                "YYYY-MM-DD"
              ),
            }
          : {}),
        ...(formState.dataPeriod !== ODataPeriod.DAY
          ? { monthOrQuarter: parseInt(formState.monthOrQuarter) }
          : {}),
        ...(formState.dataPeriod !== ODataPeriod.DAY
          ? { year: formState.year }
          : {}),
        currency: formState.currencyCode,
        invoiceSerial: serial,
      }).then((data: SummaryDataDetails[]) => {
        if (id)
          setSummaryDetails(
            combineSummaryDetails(
              combineSummaryDetails(summaryDetails, currSummaryDetails),
              data
            )
          );
        else setSummaryDetails(combineSummaryDetails(summaryDetails, data));
        setLookupData([]);
        reloadLazyData();
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lookupData, formState, id]);

  useEffect(() => {
    if (id && isEditMode) {
      InvoiceSummaryService.getInvoiceSummaryById(id).then((data) => {
        if (data.length > 0) {
          setCurrSummaryDetails(data);
          setSummaryDetails(data);
          reloadLazyData();
        }
      });
    } else {
      setSummaryDetails([]);
      reloadLazyData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEditMode]);

  return (
    <>
      <div className='grid w-full'>
        <div className='col-12'>{renderToolbar()}</div>
        <div className='col-12'>{renderDataTable()}</div>
      </div>
      <InvoiceLookUpTableDialog
        open={openLookup}
        onHide={setOpenLookup}
        setLookupData={setLookupData}
        filterData={{
          year: formState.year,
          multiTax: formState.multiTax,
          dataPeriod: formState.dataPeriod,
          dataPeriodDate: formState.dataPeriodDate,
          monthOrQuarter: formState.monthOrQuarter,
          currencyCode: formState.currencyCode,
          serial: serial,
        }}
      />
      {renderImExExcel()}
    </>
  );
};
